import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Lock, RefreshCw, Unlock } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useQueryParam, useUpdateQuery } from '../../app/useQueryParam';
import type { ThreatElementDocument } from '../../api-utils/elements';
import type { ThreatEventItem } from '../../api-utils/threats';
import { entityRiskElementId, type EntityRiskScoreDocument } from '../../api-utils/entity_risk';
import { blastElementId, type BlastRadiusScoreDocument } from '../../api-utils/blast';
import { useBlastRiskData, useEntityRiskData, useWorkspaceGraphData } from '../../data/queries/workspaceData';
import { campaignMemberActionIds, flattenIdList, linkTargetId, sourceRefId } from '../../lib/funnel';
import {
  DomainLegend,
  EntityGraphLegend,
  ForceGraphDiagram,
  type ForceGraphData,
  type ForceGraphLink,
  type ForceGraphNode,
} from './correlationGraph';
import {
  eventForThreatElement,
  selectCampaignChoices,
  toGraphSeverity,
} from '../../data/selectors/workspace';
import AttackPathStepper from './AttackPathStepper';
import TimeScrubber, {
  defaultTimelineWindow,
  parseIsoMs,
  windowFromTimes,
  type TimeScrubberMarker,
} from './TimeScrubber';
import { scoreCls, toScoreNumber } from './eriUtils';
import { attackPathNumbers, securityEventMentionsNode } from './incidentScope';
import { hopSubgraph, projectScopedGraph } from './graphProjection';
import EntityRiskIndex from './EntityRiskIndex';
import { useFeedData } from '../../data/queries/feedData';
import NodeCard from './NodeCard';
import NodeDossier from './NodeDossier';
import { CrownJewelLine } from './NodeCard';
import { crownJewelNames } from './graphProjection';
import Dialog from '../../ui/Dialog';
import { lookupByNodeId } from './dossierModel';
import { campaignLabel, incidentLabel } from '../../lib/ids';
import EmptyState from '../../ui/EmptyState';
import ErrorState from '../../ui/ErrorState';
import Skeleton from '../../ui/Skeleton';
import { useWorkspace } from '../../workspace';
import { useResizableRail } from './useResizableRail';

/** `?ws=` in the address. `risk` is the Threat Index. */
type Pane = 'graph' | 'risk';
const PANES: readonly Pane[] = ['graph', 'risk'];
const HOP_PARAMS: readonly ('1' | '2')[] = ['1', '2'];
type ScopeType = 'threat' | 'campaign' | 'entity' | 'everything';

interface Scope {
  type: ScopeType;
  id: string;
  label: string;
}

interface InvestigationWorkspaceProps {
  onOpenIncident: (id: string) => void;
  onOpenEntity: (name: string) => void;
  /** False for an entity that has no dossier to open: the button is then not offered. Defaults to true. */
  canOpenEntity?: (name: string) => boolean;
  onOpenFeed?: () => void;
  /** Open full-page EBR detail (map + recalculate). */
  onOpenEbrDetail?: (score: BlastRadiusScoreDocument) => void;
  /** Focus an entity in the graph after returning from EBR detail. */
  externalFocus?: { id: string; label: string } | null;
  onExternalFocusConsumed?: () => void;
  /** One-shot scope from Threat Feed (campaign / threat / entity), and the view to show it in (the graph by default). */
  externalScope?: (Scope & { pane?: Pane }) | null;
  onExternalScopeConsumed?: () => void;
}

function parseScopeParam(raw: string | null): Scope | null {
  if (!raw) return null;
  const [type, ...rest] = raw.split(':');
  const id = rest.join(':');
  if (!id) return { type: 'everything', id: '', label: 'Everything' };
  if (type === 'threat' || type === 'campaign' || type === 'entity') {
    return { type, id, label: id };
  }
  return null;
}

/** Value of `?scope=` for a scope; `null` when the address should not carry one. */
function scopeParamValue(scope: Scope): string | null {
  return scope.type === 'everything' ? null : `${scope.type}:${scope.id}`;
}

function isActiveIncident(event: ThreatEventItem): boolean {
  if (event.folded) return false;
  const status = String(event.detection_status || 'active').toLowerCase();
  return status === 'active' || status === 'in_progress' || status === 'reopened' || status === '';
}

function matchesQuery(query: string, parts: Array<string | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return parts.some((part) => String(part || '').toLowerCase().includes(q));
}

const SCOPE_LABEL: Record<ScopeType, string> = {
  threat: 'Incident',
  campaign: 'Campaign',
  entity: 'Entity',
  everything: 'Everything',
};

const HOME_SCOPE: Scope = { type: 'everything', id: '', label: 'Everything' };

function sameScope(a: Scope, b: Scope): boolean {
  return a.type === b.type && a.id === b.id;
}

function hopEndpointId(end: unknown): string {
  if (end == null) return '';
  if (typeof end === 'object') {
    const obj = end as { id?: string; _id?: string };
    return String(obj.id || obj._id || '');
  }
  return String(end);
}

function incidentTimeMs(event: ThreatEventItem): number | null {
  return (
    parseIsoMs(event.first_event_time) ||
    parseIsoMs(event.created_at) ||
    parseIsoMs(event.last_event_time)
  );
}

function threatElementNode(el: ThreatElementDocument): ForceGraphNode | null {
  const id = String(el._id || '').trim();
  if (!id) return null;
  const type = String(el.element_type || 'unknown');
  const name = String(el.name || '').trim();
  return {
    id,
    label: name || id,
    group: type,
    universalType: type,
    universalDomain: type,
    provider: 'threat',
    providerType: type,
    externalId: name,
    name,
    linkCount: 0,
    layer: 'threat',
  };
}

function neighborIdsFromThreat(el: ThreatElementDocument): string[] {
  const out: string[] = [];
  const take = (raw: unknown) => {
    if (!Array.isArray(raw)) return;
    for (const item of raw) {
      const id = linkTargetId(item);
      if (id && !id.startsWith('_dangling/')) out.push(id);
    }
  };
  take(el.links);
  take(el.infrastructure_refs);
  take(el.business_refs);
  return Array.from(new Set(out));
}

function graphWithScopeOrigin(
  graph: ForceGraphData,
  scope: Scope,
  threatElements: ThreatElementDocument[],
  actionByThreatId: Map<string, ThreatElementDocument>
): { graph: ForceGraphData; origins: string[]; focusOrigins: string[] } {
  if (scope.type === 'everything' || !scope.id) {
    return { graph, origins: [], focusOrigins: [] };
  }
  if (scope.type === 'entity') {
    return { graph, origins: [scope.id], focusOrigins: [scope.id] };
  }

  const byId = new Map(
    threatElements.filter((el) => el._id).map((el) => [String(el._id), el])
  );
  const element =
    scope.type === 'threat'
      ? actionByThreatId.get(scope.id) || byId.get(scope.id)
      : byId.get(scope.id);

  const origins: string[] = [];
  const focusOrigins: string[] = [];
  if (element?._id) {
    origins.push(element._id);
    focusOrigins.push(element._id);
  } else {
    origins.push(scope.id);
    focusOrigins.push(scope.id);
  }

  if (scope.type === 'campaign' && element?._id) {
    const members = campaignMemberActionIds(element).filter(Boolean);
    if (members.length > 0) {
      origins.length = 0;
      focusOrigins.length = 0;
      for (const memberId of members) {
        origins.push(memberId);
        focusOrigins.push(memberId);
      }
    }
  }

  const existing = new Set(graph.nodes.map((node) => node.id));
  const layerById = new Map(graph.nodes.map((node) => [node.id, node.layer]));
  const extraNodes: ForceGraphNode[] = [];
  const extraLinks: ForceGraphLink[] = [];
  const seenLink = new Set<string>();

  const inject = (el?: ThreatElementDocument) => {
    if (!el?._id || existing.has(el._id)) return;
    const node = threatElementNode(el);
    if (!node) return;
    extraNodes.push(node);
    existing.add(el._id);
    layerById.set(el._id, 'threat');
    for (const targetId of neighborIdsFromThreat(el)) {
      if (!existing.has(targetId)) continue;
      const key = `${el._id}\u0000${targetId}`;
      if (seenLink.has(key)) continue;
      seenLink.add(key);
      const targetLayer = layerById.get(targetId);
      extraLinks.push({
        source: el._id,
        target: targetId,
        label: 'RELATES_TO',
        crossLayer: targetLayer === 'infrastructure' || targetLayer === 'business',
      });
    }
  };

  inject(element);
  for (const id of origins) {
    if (id !== element?._id) inject(byId.get(id));
  }

  if (extraNodes.length === 0 && extraLinks.length === 0) {
    return { graph, origins, focusOrigins };
  }

  return {
    graph: {
      ...graph,
      nodes: extraNodes.length ? [...graph.nodes, ...extraNodes] : graph.nodes,
      links: extraLinks.length ? [...graph.links, ...extraLinks] : graph.links,
    },
    origins,
    focusOrigins,
  };
}

const InvestigationWorkspace: React.FC<InvestigationWorkspaceProps> = ({
  onOpenIncident,
  onOpenEntity,
  canOpenEntity,
  onOpenFeed,
  onOpenEbrDetail,
  externalFocus,
  onExternalFocusConsumed,
  externalScope,
  onExternalScopeConsumed,
}) => {
  const { org } = useWorkspace();
  const [searchParams] = useSearchParams();
  const updateQuery = useUpdateQuery();
  const scopeParam = searchParams.get('scope');
  const [pane, setPane] = useQueryParam<Pane>('ws', 'graph', PANES);
  // The incidents and campaigns of the feed, which the Threat Index filters its assets by.
  const { rows: feedRows } = useFeedData();
  // The Threat Feed's View in Index shows the index of that incident or campaign alone; any other way to the index lists all.
  const [indexScoped, setIndexScoped] = useState(false);
  const [nav, setNav] = useState<{ stack: Scope[]; index: number }>(() => {
    const start = parseScopeParam(scopeParam) || HOME_SCOPE;
    return { stack: [start], index: 0 };
  });
  const [hopParam, setHopParam] = useQueryParam<'1' | '2'>('hops', '1', HOP_PARAMS);
  const hopDepth = hopParam === '2' ? 2 : 1;
  const setHopDepth = (depth: 1 | 2) => setHopParam(depth === 2 ? '2' : '1');
  const [graphLocked, setGraphLocked] = useState(false);
  // Bumped when the graph should be laid out afresh (Refresh, a new organization); otherwise it keeps its place.
  const [layoutKey, setLayoutKey] = useState(0);
  const attackRail = useResizableRail('c3-attack-rail-width');
  const scope = nav.stack[nav.index] ?? HOME_SCOPE;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const canGoBack = nav.index > 0;
  const canGoForward = nav.index < nav.stack.length - 1;
  const [selectedNode, setSelectedNode] = useState<ForceGraphNode | null>(null);
  // The full dossier is a dialog opened only by the Details button of the node card: the places that select a node by
  // code (View in Graph, the scope menu, Back and Forward, Refresh) never open it.
  const [detailsOpen, setDetailsOpen] = useState(false);
  useEffect(() => {
    if (!selectedNode) setDetailsOpen(false);
  }, [selectedNode]);
  const [enabledInfraDomains, setEnabledInfraDomains] = useState<Set<string>>(new Set());
  // The graph is quiet at rest; the palette of the domains is the person's to switch on.
  const [domainColorsEnabled, setDomainColorsEnabled] = useState(false);
  // The records come from the shared cache and the one refresh clock (docs/c3/decisions/002-data-layer.md).
  const workspaceData = useWorkspaceGraphData();
  const {
    graph: graphData,
    threatElements,
    threatEvents,
    securityEvents,
    refresh: refreshData,
  } = workspaceData;
  const eri = useEntityRiskData();
  const ebr = useBlastRiskData();
  const entityRiskScores: EntityRiskScoreDocument[] = eri.rows;
  const blastRadiusScores: BlastRadiusScoreDocument[] = ebr.rows;
  const [manualRefresh, setManualRefresh] = useState(false);
  const graphLoading = workspaceData.loading || manualRefresh;
  const graphError = workspaceData.error ? 'Could not load the graph' : null;
  const eriLoading = eri.loading;
  const eriError = eri.error ? 'Could not load the risk scores' : null;
  const [scopeOpen, setScopeOpen] = useState(false);
  const [browseType, setBrowseType] = useState<ScopeType>('everything');
  const [scopeQuery, setScopeQuery] = useState('');
  const [scrubMs, setScrubMs] = useState(() => Date.now());
  const scopeMenuRef = useRef<HTMLDivElement>(null);

  // A new organization starts from nothing selected and every domain shown.
  const seenDomains = useRef<Set<string>>(new Set());
  useEffect(() => {
    seenDomains.current = new Set();
    setSelectedNode(null);
    setEnabledInfraDomains(new Set());
    setLayoutKey((key) => key + 1);
  }, [org]);

  // A domain the graph shows for the first time is switched on. A refresh on the clock keeps the
  // domains the person has switched off, and the node they have selected.
  useEffect(() => {
    const fresh = Array.from(
      new Set(graphData.nodes.filter((n) => n.layer === 'infrastructure').map((n) => n.group))
    ).filter((domain) => !seenDomains.current.has(domain));
    if (fresh.length === 0) return;
    fresh.forEach((domain) => seenDomains.current.add(domain));
    setEnabledInfraDomains((prev) => new Set([...Array.from(prev), ...fresh]));
  }, [graphData]);

  const refreshAll = useCallback(() => {
    setSelectedNode(null);
    setLayoutKey((key) => key + 1);
    setManualRefresh(true);
    void refreshData().finally(() => setManualRefresh(false));
  }, [refreshData]);

  // Scope -> address. Only this effect writes `?scope=`, and it skips a write that changes nothing.
  useEffect(() => {
    const wanted = scopeParamValue(scope);
    updateQuery((params) => {
      if (wanted === null) params.delete('scope');
      else params.set('scope', wanted);
    });
  }, [scope, updateQuery]);

  useEffect(() => {
    if (!scopeOpen) return;
    setBrowseType(scope.type);
    const onDoc = (e: MouseEvent) => {
      if (scopeMenuRef.current && !scopeMenuRef.current.contains(e.target as Node)) {
        setScopeOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setScopeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [scopeOpen, scope.type]);

  const { scopedGraph, attenuatedNodeIds } = useMemo(() => {
    if (scope.type === 'everything' || !scope.id) {
      return {
        scopedGraph: projectScopedGraph({
          graph: graphData,
          scopeType: 'everything',
          originIds: [],
          hopDepth,
          threatElements,
        }),
        attenuatedNodeIds: new Set<string>(),
      };
    }
    const actionByThreat = new Map<string, ThreatElementDocument>();
    for (const el of threatElements) {
      if (String(el.element_type || '').toLowerCase() !== 'action') continue;
      const tid = sourceRefId(el.threat_event_id);
      if (tid) actionByThreat.set(tid, el);
    }
    const prepared = graphWithScopeOrigin(graphData, scope, threatElements, actionByThreat);
    return {
      scopedGraph: projectScopedGraph({
        graph: prepared.graph,
        scopeType: scope.type,
        originIds: prepared.origins,
        hopDepth,
        threatElements,
      }),
      attenuatedNodeIds: new Set<string>(),
    };
  }, [graphData, scope, hopDepth, threatElements]);

  const nodeForScope = useCallback(
    (next: Scope): ForceGraphNode | null => {
      if (!next.id) return null;
      const fromGraph = graphData.nodes.find((node) => node.id === next.id);
      if (fromGraph) return fromGraph;
      const el =
        threatElements.find((item) => item._id === next.id) ||
        threatElements.find(
          (item) =>
            String(item.element_type || '').toLowerCase() === 'action' &&
            sourceRefId(item.threat_event_id) === next.id
        );
      return el ? threatElementNode(el) : null;
    },
    [graphData.nodes, threatElements]
  );

  const commitScope = useCallback(
    (next: Scope, node?: ForceGraphNode | null) => {
      setNav((prev) => {
        const current = prev.stack[prev.index] ?? HOME_SCOPE;
        if (sameScope(current, next)) {
          if (current.label === next.label) return prev;
          const stack = prev.stack.slice();
          stack[prev.index] = { ...current, label: next.label };
          return { ...prev, stack };
        }
        const stack = [...prev.stack.slice(0, prev.index + 1), next];
        return { stack, index: stack.length - 1 };
      });
      setSelectedNode(node !== undefined ? node : nodeForScope(next));
    },
    [nodeForScope]
  );

  const goNav = useCallback(
    (delta: -1 | 1) => {
      setNav((prev) => {
        const index = prev.index + delta;
        if (index < 0 || index >= prev.stack.length) return prev;
        return { ...prev, index };
      });
    },
    []
  );

  // Address -> scope, for the browser's Back and Forward and for a shared link opened in the app.
  // It runs only when the address changes: a scope picked in the workspace reaches the address a
  // render later, and comparing earlier would undo the pick.
  useEffect(() => {
    const target = parseScopeParam(scopeParam) ?? HOME_SCOPE;
    if (!sameScope(target, scopeRef.current)) commitScope(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeParam]);

  useEffect(() => {
    setSelectedNode(nodeForScope(scope));
  }, [scope.id, scope.type, nodeForScope]);

  useEffect(() => {
    if (scope.type === 'campaign' && hopDepth < 2) setHopDepth(2);
    // Only a change of scope type raises the depth; the picker may lower it afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.type]);

  const selectedThreatElement = useMemo(() => {
    if (!selectedNode || selectedNode.layer !== 'threat') return null;
    return threatElements.find((el) => el._id === selectedNode.id) || null;
  }, [selectedNode, threatElements]);

  const selectedThreatEvent = useMemo(() => {
    const tid = sourceRefId(selectedThreatElement?.threat_event_id);
    if (!tid) return null;
    return threatEvents.find((event) => event._id === tid) || null;
  }, [selectedThreatElement, threatEvents]);

  const allDomains = useMemo(
    () => Array.from(new Set(scopedGraph.nodes.map((n) => n.group))).sort(),
    [scopedGraph.nodes]
  );

  const infraDomains = useMemo(
    () =>
      Array.from(
        new Set(
          scopedGraph.nodes.filter((n) => n.layer === 'infrastructure').map((n) => n.group)
        )
      ).sort(),
    [scopedGraph.nodes]
  );

  const attenuatedInfraDomains = useMemo(() => {
    if (enabledInfraDomains.size === 0 || enabledInfraDomains.size >= infraDomains.length) {
      return new Set<string>();
    }
    return new Set(infraDomains.filter((domain) => !enabledInfraDomains.has(domain)));
  }, [enabledInfraDomains, infraDomains]);

  const toggleInfraDomain = useCallback(
    (domain: string) => {
      setEnabledInfraDomains((prev) => {
        const allEnabled =
          prev.size === 0 || infraDomains.length === 0 || infraDomains.every((d) => prev.has(d));
        if (allEnabled) return new Set([domain]);
        const next = new Set(prev);
        if (next.has(domain)) {
          next.delete(domain);
          if (next.size === 0) return new Set(infraDomains);
          return next;
        }
        next.add(domain);
        if (next.size >= infraDomains.length) return new Set(infraDomains);
        return next;
      });
    },
    [infraDomains]
  );

  const actionById = useMemo(() => {
    const map = new Map<string, ThreatElementDocument>();
    for (const el of threatElements) {
      if (el._id) map.set(el._id, el);
    }
    return map;
  }, [threatElements]);

  const actionByThreatId = useMemo(() => {
    const map = new Map<string, ThreatElementDocument>();
    for (const el of threatElements) {
      if (String(el.element_type || '').toLowerCase() !== 'action') continue;
      const tid = sourceRefId(el.threat_event_id);
      if (tid) map.set(tid, el);
    }
    return map;
  }, [threatElements]);

  const eventById = useMemo(
    () => new Map(threatEvents.map((event) => [event._id, event])),
    [threatEvents]
  );

  const scopedActionId = useMemo(() => {
    if (scope.type !== 'threat') return '';
    const el = actionById.get(scope.id) || actionByThreatId.get(scope.id);
    return el?._id || '';
  }, [scope, actionById, actionByThreatId]);

  /** Campaign scope: union of member threat_events + security events (chronological). */
  const campaignScopeData = useMemo(() => {
    if (scope.type !== 'campaign' || !scope.id) return null;
    const campaign = actionById.get(scope.id);
    if (!campaign) return null;

    type MemberRow = {
      action: ThreatElementDocument;
      event: ThreatEventItem;
      label: string;
      ms: number;
    };
    const members: MemberRow[] = [];
    for (const memberId of campaignMemberActionIds(campaign)) {
      const action = actionById.get(memberId);
      const event = eventForThreatElement(action, eventById);
      if (!action?._id || !event?._id) continue;
      const incidentMs = incidentTimeMs(event);
      const createdMs = Date.parse(event.created_at || '');
      members.push({
        action,
        event,
        label: String(action.name || '').trim() || incidentLabel(event._id),
        ms: incidentMs != null ? incidentMs : Number.isFinite(createdMs) ? createdMs : 0,
      });
    }
    members.sort((a, b) => a.ms - b.ms || a.event._id.localeCompare(b.event._id));

    const seToAction = new Map<string, string>();
    const seIds = new Set<string>();
    const threatLabels: Record<string, string> = {};
    const mitre = new Set<string>();

    const takeTags = (raw: unknown) => {
      if (!Array.isArray(raw)) return;
      for (const tag of raw) {
        const t = String(tag || '').trim();
        if (t) mitre.add(t);
      }
    };
    takeTags((campaign as { mitre_tags?: unknown }).mitre_tags);

    members.forEach((row, index) => {
      threatLabels[row.event._id] = `${index + 1}. ${row.label}`;
      takeTags((row.action as { mitre_tags?: unknown }).mitre_tags);
      for (const sid of flattenIdList(row.event.security_event_ids)) {
        seIds.add(sid);
        if (!seToAction.has(sid)) seToAction.set(sid, threatLabels[row.event._id]);
      }
    });

    const events = securityEvents
      .filter((event) => seIds.has(event._id))
      .sort((a, b) => {
        const am = Date.parse(a.event_time || a.ingestion_time || '') || 0;
        const bm = Date.parse(b.event_time || b.ingestion_time || '') || 0;
        return am - bm;
      });

    const firstTimes = members
      .map((row) => row.event.first_event_time || row.event.created_at)
      .filter(Boolean) as string[];
    const lastTimes = members
      .map((row) => row.event.last_event_time || row.event.first_event_time)
      .filter(Boolean) as string[];
    firstTimes.sort((a, b) => Date.parse(a) - Date.parse(b));
    lastTimes.sort((a, b) => Date.parse(a) - Date.parse(b));

    const securityEventIds: string[] = [];
    seIds.forEach((id) => {
      securityEventIds.push(id);
    });

    const synthetic: ThreatEventItem = {
      _id: scope.id,
      candidate_id: '',
      detection_classification:
        String(campaign.name || '').trim() || campaignLabel(scope.id),
      first_event_time: firstTimes[0],
      last_event_time: lastTimes[lastTimes.length - 1],
      created_at: firstTimes[0],
      security_event_ids: securityEventIds,
      is_campaign: true,
    };

    const stepNotes: Record<string, string> = {};
    seToAction.forEach((label, sid) => {
      stepNotes[sid] = label;
    });

    return {
      campaign,
      members: members.map((row, index) => ({
        actionId: String(row.action._id),
        threatEventId: row.event._id,
        label: row.label,
        step: index + 1,
        firstEventTime: row.event.first_event_time,
        lastEventTime: row.event.last_event_time,
        riskLevel: row.event.detection_risk_level,
      })),
      memberThreats: members.map((row) => row.event),
      events,
      synthetic,
      threatLabels,
      stepNotes,
      mitreTags: Array.from(mitre),
    };
  }, [scope.type, scope.id, actionById, eventById, securityEvents]);

  const scopedThreatEvent = useMemo(() => {
    if (scope.type === 'campaign' && campaignScopeData) {
      return campaignScopeData.synthetic;
    }
    if (scope.type === 'threat') {
      return (
        eventById.get(scope.id) ||
        eventForThreatElement(actionById.get(scope.id) || actionByThreatId.get(scope.id), eventById) ||
        null
      );
    }
    return selectedThreatEvent;
  }, [
    scope,
    eventById,
    actionById,
    actionByThreatId,
    selectedThreatEvent,
    campaignScopeData,
  ]);

  const scopedSecurityEvents = useMemo(() => {
    if (scope.type === 'campaign' && campaignScopeData) {
      return campaignScopeData.events;
    }
    if (!scopedThreatEvent) return [];
    const ids = new Set(flattenIdList(scopedThreatEvent.security_event_ids));
    return securityEvents.filter((event) => ids.has(event._id));
  }, [scope.type, campaignScopeData, scopedThreatEvent, securityEvents]);

  const attackPathFocusIds = useMemo(() => {
    if (scope.type !== 'threat') return null;
    if (!selectedNode || selectedNode.layer === 'threat') return null;
    const matched = scopedSecurityEvents.filter((event) =>
      securityEventMentionsNode(event, selectedNode)
    );
    return new Set(matched.map((event) => event._id));
  }, [scope.type, selectedNode, scopedSecurityEvents]);

  const pathMitreTags = useMemo(() => {
    if (scope.type === 'campaign' && campaignScopeData) {
      return campaignScopeData.mitreTags;
    }
    const el = scope.type === 'threat' ? actionById.get(scopedActionId) : selectedThreatElement;
    const tags = (el as { mitre_tags?: unknown } | null | undefined)?.mitre_tags;
    return Array.isArray(tags) ? (tags as string[]) : undefined;
  }, [scope.type, campaignScopeData, actionById, scopedActionId, selectedThreatElement]);

  /** Campaign dossier sections only when the campaign (or nothing) is selected. */
  const dossierShowsCampaign = useMemo(() => {
    if (scope.type !== 'campaign' || !campaignScopeData) return false;
    if (!selectedNode) return true;
    if (selectedNode.id === scope.id) return true;
    return String(selectedNode.universalType || '').toLowerCase() === 'campaign';
  }, [scope.type, scope.id, campaignScopeData, selectedNode]);

  const timelineIncidents = useMemo(() => {
    const seen = new Map<string, ThreatEventItem>();
    const add = (event?: ThreatEventItem) => {
      if (event?._id && !seen.has(event._id)) seen.set(event._id, event);
    };
    if (scope.type === 'threat') {
      add(eventById.get(scope.id));
      add(eventForThreatElement(actionById.get(scope.id) || actionByThreatId.get(scope.id), eventById));
      for (const node of scopedGraph.nodes) {
        if (node.layer !== 'threat') continue;
        add(eventForThreatElement(actionById.get(node.id), eventById));
      }
    } else if (scope.type === 'campaign') {
      const campaign = actionById.get(scope.id);
      if (campaign) {
        for (const memberId of campaignMemberActionIds(campaign)) {
          add(eventForThreatElement(actionById.get(memberId), eventById));
        }
      }
    } else if (scope.type === 'entity' && scope.id) {
      const nearby = hopSubgraph(graphData, scope.id, 2);
      for (const node of nearby.nodes) {
        if (node.layer !== 'threat') continue;
        add(eventForThreatElement(actionById.get(node.id), eventById));
      }
    }
    return Array.from(seen.values());
  }, [scope, eventById, actionById, actionByThreatId, scopedGraph, graphData]);

  const timeline = useMemo(() => {
    const times = timelineIncidents
      .map(incidentTimeMs)
      .filter((ms): ms is number => ms != null);
    if (times.length === 0) {
      const idle = defaultTimelineWindow();
      return { ...idle, filters: false, markers: [] as TimeScrubberMarker[] };
    }
    const win = windowFromTimes(times);
    return {
      ...win,
      filters: true,
      markers: times.map((at) => ({ at, kind: 'incident' as const })),
    };
  }, [timelineIncidents]);

  useEffect(() => {
    setScrubMs(timeline.max);
  }, [timeline.min, timeline.max, scope.id, scope.type]);

  const displayedGraph = useMemo(() => {
    if (!timeline.filters) return scopedGraph;
    const keep = new Set<string>();
    for (const node of scopedGraph.nodes) {
      if (node.layer !== 'threat') {
        keep.add(node.id);
        continue;
      }
      const el = actionById.get(node.id);
      const event = eventForThreatElement(el, eventById);
      const ms = event ? incidentTimeMs(event) : null;
      if (ms == null || ms <= scrubMs) keep.add(node.id);
    }
    const nodes = scopedGraph.nodes.filter((node) => keep.has(node.id));
    const links = scopedGraph.links.filter(
      (link) => keep.has(hopEndpointId(link.source)) && keep.has(hopEndpointId(link.target))
    );
    return {
      nodes,
      links,
      stats: {
        ...scopedGraph.stats,
        nodeCount: nodes.length,
        linkCount: links.length,
        crossLayerLinkCount: links.filter((link) => link.crossLayer).length,
      },
    };
  }, [scopedGraph, timeline.filters, scrubMs, actionById, eventById]);
  // The same condition as the graph's skeleton and error state below.
  const graphUnread = (graphLoading && displayedGraph.nodes.length === 0) || Boolean(graphError);

  const diagramGraph = useMemo(() => {
    // Sequence badges only in campaign scope, and only on that campaign's member actions.
    if (scope.type !== 'campaign' || !scope.id) return displayedGraph;
    const campaign = actionById.get(scope.id);
    if (!campaign) return displayedGraph;
    const memberIds = new Set(campaignMemberActionIds(campaign).filter(Boolean));
    if (memberIds.size < 2) return displayedGraph;

    const memberNodes = displayedGraph.nodes.filter(
      (node) =>
        memberIds.has(node.id) &&
        node.layer === 'threat' &&
        String(node.universalType || '').toLowerCase() === 'action'
    );
    const timeByNodeId = new Map<string, number>();
    for (const node of memberNodes) {
      const event = eventForThreatElement(actionById.get(node.id), eventById);
      const ms = event ? incidentTimeMs(event) : null;
      if (ms != null) timeByNodeId.set(node.id, ms);
    }
    const order = attackPathNumbers(memberNodes, timeByNodeId);
    if (order.size === 0) return displayedGraph;
    return {
      ...displayedGraph,
      nodes: displayedGraph.nodes.map((node) => {
        const attackStep = order.get(node.id);
        return attackStep ? { ...node, attackStep } : node;
      }),
    };
  }, [displayedGraph, scope.type, scope.id, actionById, eventById]);

  const eriByElement = useMemo(() => {
    const map = new Map<string, EntityRiskScoreDocument>();
    for (const row of entityRiskScores) {
      const id = entityRiskElementId(row);
      if (id) map.set(id, row);
    }
    return map;
  }, [entityRiskScores]);

  const ebrByElement = useMemo(() => {
    const map = new Map<string, BlastRadiusScoreDocument>();
    for (const row of blastRadiusScores) {
      const id = blastElementId(row);
      if (id) map.set(id, row);
    }
    return map;
  }, [blastRadiusScores]);

  const selectedEri =
    selectedNode?.layer === 'infrastructure'
      ? lookupByNodeId(eriByElement, selectedNode.id)
      : undefined;
  const selectedEbr =
    selectedNode?.layer === 'infrastructure'
      ? lookupByNodeId(ebrByElement, selectedNode.id)
      : undefined;

  const eriScopeIds = useMemo(() => {
    if (scope.type === 'everything' || !scope.id) return null;
    const ids = new Set<string>();
    for (const node of scopedGraph.nodes) {
      if (node.layer === 'infrastructure' || node.layer === 'business') ids.add(node.id);
    }
    return ids;
  }, [scope.type, scope.id, scopedGraph.nodes]);

  const incidentChoices = useMemo(() => {
    return threatEvents
      .filter(isActiveIncident)
      .filter((event) =>
        matchesQuery(scopeQuery, [
          event._id,
          incidentLabel(event._id),
          event.detection_description,
          event.detection_classification,
          event.detection_risk_level,
        ])
      )
      .sort(
        (a, b) =>
          Date.parse(b.last_event_time || b.created_at || '') -
          Date.parse(a.last_event_time || a.created_at || '')
      );
  }, [threatEvents, scopeQuery]);

  const campaignChoices = useMemo(() => {
    return selectCampaignChoices(threatElements, threatEvents)
      .filter((row) =>
        matchesQuery(scopeQuery, [
          row.campaign._id,
          campaignLabel(row.campaign._id || ''),
          row.campaign.name,
          ...row.members.map((event) => event._id),
          ...row.members.map((event) => incidentLabel(event._id)),
        ])
      );
  }, [threatElements, threatEvents, scopeQuery]);

  const entityChoices = useMemo(() => {
    const nearThreat = new Set<string>();
    const incidentCount = new Map<string, number>();
    for (const node of graphData.nodes) {
      if (node.layer !== 'threat') continue;
      const sub = hopSubgraph(graphData, node.id, 2);
      for (const other of sub.nodes) {
        if (other.layer === 'threat') continue;
        nearThreat.add(other.id);
        incidentCount.set(other.id, (incidentCount.get(other.id) || 0) + 1);
      }
    }
    return graphData.nodes
      .filter((node) => node.layer !== 'threat' && nearThreat.has(node.id))
      .map((node) => {
        const eri = lookupByNodeId(eriByElement, node.id);
        const score = toScoreNumber(eri?.score);
        return { node, score, incidents: incidentCount.get(node.id) || 0 };
      })
      .filter((row) =>
        matchesQuery(scopeQuery, [
          row.node.id,
          row.node.label,
          row.node.name,
          row.node.universalType,
        ])
      )
      .sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  }, [graphData, eriByElement, scopeQuery]);

  const applyEverything = () => {
    commitScope(HOME_SCOPE, null);
    setScopeOpen(false);
    setScopeQuery('');
  };

  const applyIncident = (event: ThreatEventItem) => {
    const action = actionByThreatId.get(event._id);
    const next: Scope = {
      type: 'threat',
      id: action?._id || event._id,
      label: incidentLabel(event._id),
    };
    commitScope(next, nodeForScope(next));
    setScopeOpen(false);
    setScopeQuery('');
    setPane('graph');
  };

  const applyCampaign = (campaign: ThreatElementDocument) => {
    const next: Scope = {
      type: 'campaign',
      id: campaign._id || '',
      label: campaignLabel(campaign._id || campaign.name || ''),
    };
    commitScope(next, nodeForScope(next));
    setScopeOpen(false);
    setScopeQuery('');
    setPane('graph');
  };

  const applyEntity = (node: ForceGraphNode) => {
    commitScope({ type: 'entity', id: node.id, label: node.label }, node);
    setScopeOpen(false);
    setScopeQuery('');
    setPane('graph');
  };

  // "Focus here" for the selected entity; absent for a threat node and when the entity already is the scope.
  const focusEntity =
    selectedNode && selectedNode.layer !== 'threat' && !(scope.type === 'entity' && scope.id === selectedNode.id)
      ? () => applyEntity(selectedNode)
      : undefined;

  // What the dossier dialog does goes to another view or another scope, so the dialog closes first.
  const closeThen =
    <A extends unknown[]>(action: (...args: A) => void) =>
    (...args: A) => {
      setDetailsOpen(false);
      action(...args);
    };

  useEffect(() => {
    if (!externalFocus?.id) return;
    const node = graphData.nodes.find((item) => item.id === externalFocus.id);
    if (node) {
      applyEntity(node);
    } else {
      commitScope(
        { type: 'entity', id: externalFocus.id, label: externalFocus.label },
        null
      );
      setPane('graph');
    }
    onExternalFocusConsumed?.();
    // applyEntity / commitScope are stable enough; focus is one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFocus, graphData.nodes]);

  useEffect(() => {
    if (!externalScope?.type) return;
    if (externalScope.type === 'everything') {
      commitScope(HOME_SCOPE, null);
      setIndexScoped(false);
      setPane(externalScope.pane ?? 'graph');
      onExternalScopeConsumed?.();
      return;
    }
    if (!externalScope.id) return;
    const node = nodeForScope(externalScope);
    commitScope(
      {
        type: externalScope.type,
        id: externalScope.id,
        label: externalScope.label || externalScope.id,
      },
      node
    );
    setIndexScoped(externalScope.pane === 'risk');
    setPane(externalScope.pane ?? 'graph');
    onExternalScopeConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalScope, graphData.nodes, threatElements]);

  // The card and the dossier are for an incident or a campaign (client notes 10/26, item 27). An entity is only selected: it
  // is marked in the graph, its name shows when the pointer rests on it, and what it means for the attack path stays there.
  const dossierNode = selectedNode?.layer === 'threat' ? selectedNode : null;
  // Whether that incident or campaign contains a crown jewel, which the card says in a line of its own (item 32).
  const crownJewels = useMemo(
    () => (dossierNode ? crownJewelNames(graphData, dossierNode) : undefined),
    [graphData, dossierNode]
  );

  const scopePhrase =
    scope.type === 'everything'
      ? ''
      : scope.type === 'threat'
        ? (() => {
            const dimmed = displayedGraph.nodes.filter((node) =>
              attenuatedNodeIds.has(node.id)
            ).length;
            return `Showing ${scope.label} · ${displayedGraph.nodes.length - dimmed} in the incident, ${dimmed} neighbors dimmed`;
          })()
        : `${SCOPE_LABEL[scope.type]} “${scope.label}”`;

  return (
    <section id="c3-investigation-workspace" className="c3-band">
      <h2>Investigation Workspace</h2>
      <div className="flex flex-wrap items-center gap-2 mb-3.5">
        {(
          [
            ['graph', 'Threat Graph'],
            ['risk', 'Threat Index'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`c3-pill ${pane === id ? 'active' : ''}`}
            onClick={() => {
              setIndexScoped(false);
              setPane(id);
            }}
          >
            {label}
          </button>
        ))}
        <div className="c3-scope" ref={scopeMenuRef}>
          <button
            type="button"
            className="c3-scope-btn"
            aria-haspopup="menu"
            aria-expanded={scopeOpen}
            onClick={() => setScopeOpen((open) => !open)}
          >
            <span className="c3-scope-k">Scope</span>
            <span className="c3-scope-v">{SCOPE_LABEL[scope.type]}</span>
            <ChevronDown className="w-3.5 h-3.5 c3-scope-car" />
          </button>
          {scopeOpen && (
            <div className="c3-scope-panel" role="menu">
              <div className="c3-scope-head">
                <input
                  className="c3-scope-search"
                  value={scopeQuery}
                  onChange={(e) => setScopeQuery(e.target.value)}
                  placeholder={
                    browseType === 'everything'
                      ? 'Search by name or id'
                      : `Search ${SCOPE_LABEL[browseType].toLowerCase()} by name or id`
                  }
                />
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(['threat', 'campaign', 'entity', 'everything'] as ScopeType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`c3-pill${browseType === type ? ' active' : ''}`}
                      onClick={() => {
                        if (type === 'everything') applyEverything();
                        else {
                          setBrowseType(type);
                          setScopeQuery('');
                        }
                      }}
                    >
                      {SCOPE_LABEL[type]}
                    </button>
                  ))}
                </div>
              </div>
              {browseType === 'threat' && (
                <>
                  <div className="c3-scope-caption">Open incidents in the Threat Feed</div>
                  {incidentChoices.map((event) => (
                    <button
                      key={event._id}
                      type="button"
                      className="c3-scope-row"
                      onClick={() => applyIncident(event)}
                    >
                      <span className="c3-scope-id">{incidentLabel(event._id)}</span>
                      <span className={`c3-scope-sev is-${toGraphSeverity(event.detection_risk_level)}`}>
                        {toGraphSeverity(event.detection_risk_level)}
                      </span>
                      <span className="c3-scope-copy">
                        {event.detection_description || event.detection_classification || '—'}
                      </span>
                    </button>
                  ))}
                  {incidentChoices.length === 0 && (
                    <div className="text-c3-mute text-[12px] px-1 py-2">No active incidents</div>
                  )}
                </>
              )}
              {browseType === 'campaign' && (
                <>
                  <div className="c3-scope-caption">Campaigns in the Threat Feed</div>
                  {campaignChoices.map((row) => (
                    <button
                      key={row.campaign._id || row.campaign.name}
                      type="button"
                      className="c3-scope-row"
                      onClick={() => applyCampaign(row.campaign)}
                    >
                      <span className="c3-scope-id">{campaignLabel(row.campaign._id || '')}</span>
                      <span className="c3-scope-copy">
                        {row.members.length} incident{row.members.length === 1 ? '' : 's'}
                        {row.top.length > 0
                          ? ` · ${row.top.map((event) => incidentLabel(event._id)).join(', ')}`
                          : ''}
                      </span>
                    </button>
                  ))}
                  {campaignChoices.length === 0 && (
                    <div className="text-c3-mute text-[12px] px-1 py-2">No active campaigns</div>
                  )}
                </>
              )}
              {browseType === 'entity' && (
                <>
                  <div className="c3-scope-caption">Entities with open incidents · by risk</div>
                  {entityChoices.map((row) => (
                    <button
                      key={row.node.id}
                      type="button"
                      className="c3-scope-row c3-scope-row--entity"
                      onClick={() => applyEntity(row.node)}
                    >
                      <span>
                        <span className="c3-scope-name">{row.node.label}</span>
                        <span className="c3-scope-sub">
                          {row.node.universalType || 'Entity'} · {row.incidents} incident
                          {row.incidents === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className={`c3-scope-score ${row.score == null ? 'is-empty' : scoreCls(row.score)}`}>
                        {row.score == null ? '—' : Math.round(row.score)}
                      </span>
                    </button>
                  ))}
                  {entityChoices.length === 0 && (
                    <div className="text-c3-mute text-[12px] px-1 py-2">
                      No entities linked to threats
                    </div>
                  )}
                </>
              )}
              {browseType !== 'everything' && (
                <button
                  type="button"
                  className="c3-scope-more"
                  onClick={() => {
                    setScopeOpen(false);
                    if (browseType === 'entity') setPane('risk');
                    else onOpenFeed?.();
                  }}
                >
                  Show all
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {scope.type !== 'everything' && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[13px] text-c3-mute">{scopePhrase}</span>
          <button type="button" className="c3-pill" onClick={() => applyEverything()}>
            Clear
          </button>
        </div>
      )}

      {pane === 'graph' && (
        <>
        <div className="grid grid-cols-1 gap-4">
          <div className="flex min-h-0 flex-col gap-2">
            <div className="c3-viz-panel">
              <TimeScrubber
                min={timeline.min}
                max={timeline.max}
                value={scrubMs}
                onChange={setScrubMs}
                markers={timeline.markers}
                filtersActive={timeline.filters}
                className="c3-timescrub--embedded"
              />
              <div
                {...attackRail.bodyProps}
                className={`c3-graph-body${attackRail.resizing ? ' is-resizing' : ''}`}
              >
                <div className="c3-graph-canvas relative h-full min-h-0">
                  <div className="absolute top-2.5 left-3 z-10 pointer-events-none">
                    <div className="c3-graph-titlebar pointer-events-auto">
                      <div className="c3-graph-title">Threat graph</div>
                      <button
                        type="button"
                        className="c3-graph-lock"
                        aria-pressed={graphLocked}
                        aria-label={graphLocked ? 'Unlock graph navigation' : 'Lock graph navigation'}
                        title={graphLocked ? 'Locked · inspect only' : 'Unlocked · click to scope'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setGraphLocked((prev) => !prev);
                        }}
                      >
                        {graphLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        className="c3-graph-nav"
                        disabled={!canGoBack}
                        aria-label="Previous node"
                        onClick={(e) => {
                          e.stopPropagation();
                          goNav(-1);
                        }}
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        className="c3-graph-nav"
                        disabled={!canGoForward}
                        aria-label="Next node"
                        onClick={(e) => {
                          e.stopPropagation();
                          goNav(1);
                        }}
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      refreshAll();
                    }}
                    disabled={graphLoading}
                    title="Read the data again and arrange the graph afresh"
                    className="c3-pill absolute top-2.5 right-3 z-10 disabled:opacity-50"
                  >
                    <RefreshCw size={14} strokeWidth={1.5} aria-hidden />
                    {graphLoading ? 'Loading…' : 'Refresh'}
                  </button>
                  {graphLoading && displayedGraph.nodes.length === 0 ? (
                    <div className="flex h-full items-center justify-center px-8">
                      <div className="w-full max-w-sm">
                        <Skeleton lines={4} label="Loading the graph…" />
                      </div>
                    </div>
                  ) : graphError ? (
                    <div className="flex h-full items-center justify-center">
                      <ErrorState title={graphError} onRetry={() => void refreshData()}>
                        Nothing was changed. Try again in a moment.
                      </ErrorState>
                    </div>
                  ) : scopedGraph.nodes.length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                      {scope.type === 'everything' ? (
                        <EmptyState title="No entities yet">When the system maps your environment, it appears here.</EmptyState>
                      ) : (
                        <EmptyState title="Nothing in this scope">Clear the scope to see the whole graph.</EmptyState>
                      )}
                    </div>
                  ) : displayedGraph.nodes.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-c3-mute px-6 text-center">
                      Nothing visible at this time. Move the scrubber forward.
                    </div>
                  ) : (
                    <ForceGraphDiagram
                      graphData={diagramGraph}
                      selectedNodeId={selectedNode?.id ?? null}
                      onSelectNode={(node) => {
                        if (!node) {
                          if (scope.type === 'threat') {
                            setSelectedNode(nodeForScope(scope));
                          } else {
                            setSelectedNode(null);
                          }
                          return;
                        }
                        // A click on an entity only selects it, as the client's v3 spec has it: the graph stays as it is and
                        // the node card shows the entity. "Focus here" on the card is what changes the scope. An incident
                        // or a campaign still scopes on a click.
                        if (graphLocked || node.layer !== 'threat') {
                          setSelectedNode(node);
                          return;
                        }
                        if (scope.type === 'threat') {
                          if (node.layer === 'threat') {
                            const isCampaign = String(node.universalType).toLowerCase() === 'campaign';
                            if (isCampaign) {
                              commitScope({ type: 'campaign', id: node.id, label: node.label }, node);
                              return;
                            }
                            if (scopedActionId && node.id !== scopedActionId) {
                              const related = eventForThreatElement(actionById.get(node.id), eventById);
                              commitScope(
                                {
                                  type: 'threat',
                                  id: node.id,
                                  label: incidentLabel(related?._id || node.id),
                                },
                                node
                              );
                              return;
                            }
                            setSelectedNode(node);
                            return;
                          }
                          setSelectedNode(node);
                          return;
                        }
                        const type: ScopeType =
                          String(node.universalType).toLowerCase() === 'campaign' ? 'campaign' : 'threat';
                        commitScope({ type, id: node.id, label: node.label }, node);
                      }}
                      onSelectCampaignCluster={(campaignId) => {
                        const campaign = threatElements.find((el) => el._id === campaignId);
                        if (!campaign) return;
                        if (graphLocked) {
                          setSelectedNode(
                            nodeForScope({
                              type: 'campaign',
                              id: campaignId,
                              label: campaignLabel(campaign._id || campaign.name || ''),
                            })
                          );
                          return;
                        }
                        applyCampaign(campaign);
                      }}
                      activeCampaignId={scope.type === 'campaign' ? scope.id : null}
                      attenuatedInfraDomains={attenuatedInfraDomains}
                      attenuatedNodeIds={attenuatedNodeIds}
                      domainColorsEnabled={domainColorsEnabled}
                      layoutKey={layoutKey}
                      className="h-full"
                    />
                  )}
                  {dossierNode ? (
                    <NodeCard
                      key={dossierNode.id}
                      node={dossierNode}
                      crownJewels={crownJewels}
                      eri={selectedEri}
                      ebr={selectedEbr}
                      scopedThreatEvent={dossierShowsCampaign ? scopedThreatEvent : selectedThreatEvent}
                      scopedSecurityEvents={dossierShowsCampaign ? scopedSecurityEvents : []}
                      campaignMemberCount={dossierShowsCampaign ? campaignScopeData?.members?.length : undefined}
                      onDetails={() => setDetailsOpen(true)}
                      onOpenIncident={onOpenIncident}
                      onOpenEntity={(canOpenEntity?.(dossierNode.label) ?? true) ? onOpenEntity : undefined}
                      onFocusEntity={focusEntity}
                    />
                  ) : null}
                </div>
                {attackRail.splitter}
                <aside className="c3-attack-rail">
                  <div className="c3-attack-path">
                    <div className="c3-attack-k">Attack path</div>
                    <AttackPathStepper
                      threat={scopedThreatEvent}
                      events={scopedSecurityEvents}
                      mitreTags={pathMitreTags}
                      focusEventIds={attackPathFocusIds}
                      focusLabel={
                        attackPathFocusIds && selectedNode && selectedNode.layer !== 'threat'
                          ? selectedNode.label
                          : undefined
                      }
                      stepNotes={
                        scope.type === 'campaign' ? campaignScopeData?.stepNotes : undefined
                      }
                      emptyHint={
                        scope.type === 'campaign'
                          ? 'No security events on campaign members yet.'
                          : undefined
                      }
                    />
                  </div>
                  <div className="c3-attack-legend is-natural">
                    {/* Nothing read yet, or the read failed: the legend leaves its totals out. */}
                    <EntityGraphLegend
                      stats={
                        graphUnread
                          ? undefined
                          : {
                              nodeCount: displayedGraph.stats.nodeCount,
                              linkCount: displayedGraph.stats.linkCount,
                            }
                      }
                      openIncidents={
                        graphUnread ? undefined : threatEvents.filter((event) => isActiveIncident(event)).length
                      }
                    >
                      {/* The key of the types of entity lives in the attack-path window (client notes 10/26, item 26). */}
                      {infraDomains.length > 0 ? (
                        <>
                          <div className="c3-entity-legend-k c3-entity-types-head">
                            Entity types
                            <label className="c3-domain-color-switch" title="Toggle infrastructure domain colors">
                              <span>Colors</span>
                              <button
                                type="button"
                                role="switch"
                                aria-label="Domain colors"
                                aria-checked={domainColorsEnabled}
                                className={`c3-toggle${domainColorsEnabled ? ' is-on' : ''}`}
                                onClick={() => setDomainColorsEnabled((prev) => !prev)}
                              >
                                <span className="c3-toggle-knob" />
                              </button>
                            </label>
                          </div>
                          <DomainLegend
                            domains={infraDomains}
                            colorDomains={allDomains}
                            enabledDomains={enabledInfraDomains}
                            onToggleDomain={toggleInfraDomain}
                            domainColorsEnabled={domainColorsEnabled}
                          />
                          <div className="c3-entity-types-hint">Click a type to focus it · dims the others</div>
                        </>
                      ) : null}
                    </EntityGraphLegend>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>
        {detailsOpen && dossierNode ? (
          <Dialog eyebrow="Dossier" title={dossierNode.label} size="md" onClose={() => setDetailsOpen(false)}>
            <div className="c3-dossier flex flex-col text-[13px]">
              {crownJewels ? <CrownJewelLine names={crownJewels} /> : null}
              <NodeDossier
                node={dossierNode}
                showTitle={false}
                eri={selectedEri}
                ebr={selectedEbr}
                scopedThreatEvent={dossierShowsCampaign ? scopedThreatEvent : selectedThreatEvent}
                scopedSecurityEvents={dossierShowsCampaign ? scopedSecurityEvents : []}
                campaignThreats={dossierShowsCampaign ? campaignScopeData?.memberThreats : undefined}
                campaignMembers={dossierShowsCampaign ? campaignScopeData?.members : undefined}
                threatLabels={dossierShowsCampaign ? campaignScopeData?.threatLabels : undefined}
                attackPathStepNotes={dossierShowsCampaign ? campaignScopeData?.stepNotes : undefined}
                attackPathFiltered={Boolean(
                  attackPathFocusIds && selectedNode && selectedNode.layer !== 'threat'
                )}
                onOpenIncident={closeThen(onOpenIncident)}
                onOpenEntity={(canOpenEntity?.(dossierNode.label) ?? true) ? closeThen(onOpenEntity) : undefined}
                onFocusEntity={focusEntity ? closeThen(focusEntity) : undefined}
                onOpenEbr={closeThen(() => {
                  if (selectedEbr && onOpenEbrDetail) {
                    onOpenEbrDetail(selectedEbr);
                    return;
                  }
                })}
              />
            </div>
          </Dialog>
        ) : null}
      </>
      )}

      {pane === 'risk' && (
        <EntityRiskIndex
          rows={entityRiskScores}
          nodes={graphData.nodes}
          scopeIds={eriScopeIds}
          scopeOnlyInitially={indexScoped}
          activity={feedRows}
          loading={eriLoading}
          error={eriError}
          onRetry={() => void refreshData()}
          onOpenEntity={(id, name) => {
            const node = graphData.nodes.find((item) => item.id === id);
            if (node) {
              applyEntity(node);
              return;
            }
            commitScope({ type: 'entity', id, label: name });
            setPane('graph');
          }}
        />
      )}

    </section>
  );
};

export default InvestigationWorkspace;
